import React from 'react';
import { Settings as SettingsIcon } from '@mui/icons-material';
import type { NavigationMenuItem } from './navigationMenu';

export interface AdminHubItem {
  text: string;
  path: string;
  description: string;
  /** Visible solo para SUPERADMIN (alineado al buscador de accesos). */
  superAdminOnly?: boolean;
}

export type AdminHubCategoryId =
  | 'mi-empresa'
  | 'usuarios-cajas'
  | 'ventas-comprobantes'
  | 'depositos-logistica'
  | 'integraciones'
  | 'moneda-domicilios'
  | 'tenants';

export interface AdminHubCategory {
  id: AdminHubCategoryId;
  title: string;
  description: string;
  color: string;
  items: AdminHubItem[];
}

export const administrationMenuItem: NavigationMenuItem = {
  text: 'Administración',
  icon: React.createElement(SettingsIcon),
  path: '/configuraciones',
};

/** Rutas de maestros bajo /sistema (sin índice propio; el menú es el hub de Administración). */
export const sistemaModulePath = '/sistema';

export const adminHubCategories: AdminHubCategory[] = [
  {
    id: 'mi-empresa',
    title: 'Mi empresa',
    description: 'Datos fiscales y correo compartido para facturas.',
    color: '#0ea5e9',
    items: [
      {
        text: 'Compañías',
        path: '/sistema/companias',
        description: 'Datos fiscales y razón social de la empresa.',
      },
      {
        text: 'Certificados ARCA',
        path: '/sistema/certificados-arca',
        description: 'Certificados, claves y servicios web para facturación electrónica.',
      },
      {
        text: 'Envio de Correos',
        path: '/configuraciones/correo-salida',
        description: 'Cuenta SMTP y remitentes de los envíos por correo.',
      },
      {
        text: 'Parámetros del sistema',
        path: '/configuraciones/parametros',
        description: 'Valores de configuración de la empresa (solo edición).',
      },
      {
        text: 'Variables del sistema',
        path: '/configuraciones/variables-sistema',
        description: 'Variables Varsis2 de la empresa (solo edición de valor y comentario).',
      },
    ],
  },
  {
    id: 'usuarios-cajas',
    title: 'Usuarios y cajas',
    description: 'Accesos del equipo y asignación de cajas por usuario.',
    color: '#8b5cf6',
    items: [
      {
        text: 'Usuarios del tenant',
        path: '/sistema/usuarios',
        description: 'Alta, roles y permisos de usuarios vinculados al tenant actual.',
      },
      {
        text: 'Grupos de caja por usuario',
        path: '/sistema/grupo-caja-usuario',
        description: 'Grupos MCP y caja vinculados al usuario.',
        superAdminOnly: true,
      },
      {
        text: 'Grupos de caja',
        path: '/tesoreria/grupos-mcp',
        description: 'Catálogo de grupos MCP para organizar medios de cobro y pago.',
      },
      {
        text: 'Carteras',
        path: '/tesoreria/carteras',
        description: 'Cuentas bancarias y carteras vinculadas a la tesorería.',
      },
      {
        text: 'Medios MCP',
        path: '/tesoreria/medios-mcp',
        description: 'Medios de cobro y pago: efectivo, cheques, tarjetas, retenciones y otros.',
      },
    ],
  },
  {
    id: 'ventas-comprobantes',
    title: 'Comprobantes',
    description: 'Sucursales, tipos de comprobante e impuestos.',
    color: '#22a36a',
    items: [
      {
        text: 'Sucursales de venta',
        path: '/configuraciones/sucursalesventa',
        description: 'Puntos de venta y numeración.',
      },
      {
        text: 'Tipos de formulario por sucursal',
        path: '/configuraciones/sucventa',
        description: 'Comprobantes habilitados por sucursal.',
        superAdminOnly: true,
      },
      {
        text: 'Tipos de formulario',
        path: '/sistema/tipo-formulario',
        description: 'Catálogo de comprobantes del sistema.',
        superAdminOnly: true,
      },
      {
        text: 'Tipos de comprobante ARCA',
        path: '/sistema/wsfe-tipcomp',
        description: 'Mapeo de tipo de formulario y letra al código AFIP (WSFE_TipComp).',
      },
      {
        text: 'Impuestos',
        path: '/configuraciones/impuestos',
        description: 'Códigos y alícuotas de impuestos.',
      },
    ],
  },
  {
    id: 'depositos-logistica',
    title: 'Depósitos y logística',
    description: 'Ubicaciones de stock y medios de entrega.',
    color: '#f59e0b',
    items: [
      {
        text: 'Ubicaciones',
        path: '/configuraciones/ubicaciones',
        description: 'Depósitos y puntos de stock.',
      },
      {
        text: 'Transportes',
        path: '/configuraciones/transportes',
        description: 'Expresos y medios de entrega.',
      },
    ],
  },
  {
    id: 'integraciones',
    title: 'Integraciones',
    description: 'Accesos API y notificaciones HTTP a sistemas externos.',
    color: '#0f766e',
    items: [
      {
        text: 'Tokens de integración',
        path: '/configuraciones/tokens-integracion',
        description: 'Accesos API Bearer para sistemas externos.',
      },
      {
        text: 'Claves de agente (LLM)',
        path: '/configuraciones/claves-agente',
        description: 'Claves personales para OpenClaw, Claude y otros agentes.',
      },
      {
        text: 'Webhooks',
        path: '/configuraciones/webhooks',
        description: 'Suscripciones, eventos, logs y reintentos.',
      },
    ],
  },
  {
    id: 'moneda-domicilios',
    title: 'Moneda y domicilios',
    description: 'Monedas, cotizaciones y tablas geográficas.',
    color: '#0369a1',
    items: [
      {
        text: 'Monedas',
        path: '/sistema/monedas',
        description: 'Monedas del sistema.',
      },
      {
        text: 'Cotizaciones de moneda',
        path: '/sistema/movmoneda',
        description: 'Tipos de cambio por fecha.',
      },
      {
        text: 'Provincias',
        path: '/sistema/provincias',
        description: 'Provincias para domicilios.',
      },
      {
        text: 'Localidades',
        path: '/sistema/localidades',
        description: 'Ciudades y códigos postales.',
      },
    ],
  },
  {
    id: 'tenants',
    title: 'Configuración de tenants',
    description: 'Administración de empresas y tenants del sistema.',
    color: '#dc2626',
    items: [
      {
        text: 'Administración de tenants',
        path: '/sistema/tenants',
        description: 'Alta, edición y gestión de tenants.',
        superAdminOnly: true,
      },
      {
        text: 'Usuarios maestros',
        path: '/sistema/usuarios-maestros',
        description: 'ABM de usuarios en eOneMaster.Users.',
        superAdminOnly: true,
      },
      {
        text: 'Biblioteca de informes',
        path: '/sistema/biblioteca-informes',
        description: 'Store de informes RDLC: versiones y publicación.',
        superAdminOnly: true,
      },
    ],
  },
];

export function isAdministracionPath(pathname: string): boolean {
  return (
    pathname === '/configuraciones'
    || pathname.startsWith('/configuraciones/')
    || pathname.startsWith('/sistema/')
    || isTesoreriaAdminHubPath(pathname)
  );
}

/** Rutas bajo /tesoreria enlazadas desde el hub de Administración (no desde el menú Tesorería). */
export function getTesoreriaAdminHubPrefixes(): string[] {
  return adminHubCategories
    .flatMap((category) => category.items.map((item) => item.path))
    .filter((path) => path.startsWith('/tesoreria/'));
}

export function isTesoreriaAdminHubPath(pathname: string): boolean {
  return getTesoreriaAdminHubPrefixes().some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

const ADMIN_HUB_CATEGORY_IDS = new Set(adminHubCategories.map((category) => category.id));

/** Sub-hub de categoría: /configuraciones/mi-empresa (no pantallas operativas como /impuestos). */
export function isAdminHubCategoryPath(pathname: string): boolean {
  const match = pathname.match(/^\/configuraciones\/([^/]+)$/);
  if (!match) return false;
  return ADMIN_HUB_CATEGORY_IDS.has(match[1] as AdminHubCategoryId);
}

/** Destino del botón "Volver al Menu" para pantallas del hub de Administración. */
export function getAdministracionBackPath(
  pathname: string,
  preferredBack?: string | null,
): string | null {
  if (pathname === administrationMenuItem.path) return null;

  if (isAdminHubCategoryPath(pathname)) {
    return administrationMenuItem.path;
  }

  const isConfigSub = pathname.startsWith('/configuraciones/');
  const isSistema = pathname.startsWith(`${sistemaModulePath}/`);
  const isTesoreriaAdmin = isTesoreriaAdminHubPath(pathname);

  if (!isConfigSub && !isSistema && !isTesoreriaAdmin) return null;

  if (preferredBack && preferredBack !== pathname) return preferredBack;

  if (isTesoreriaAdmin) return null;

  return administrationMenuItem.path;
}

export function filterAdminHubCategories(
  categories: AdminHubCategory[],
  isSuperAdmin: boolean,
): AdminHubCategory[] {
  return categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => !item.superAdminOnly || isSuperAdmin),
    }))
    .filter((category) => category.items.length > 0);
}

export function getAdminHubCategoryById(
  categoryId: string,
  isSuperAdmin: boolean,
): AdminHubCategory | undefined {
  return filterAdminHubCategories(adminHubCategories, isSuperAdmin).find(
    (category) => category.id === categoryId,
  );
}
